import { ACTIVITY_STATUS }                             from '../constants/ProductEnums';
import { CorvalCompletionState, CorvalConstraintType } from '../types';
import { getKeyFromMapValue }                          from "@src/util";
import { BryntumConstraintType }                       from "@src/views/pages/projects/mapping/gantt/tools/TaskFunctions";


export class InteropService {

    static IncompletePercentDone = 50;

    static getActivityStatusToPercentDone( status: ACTIVITY_STATUS | CorvalCompletionState | string ): number{

        const relativePercentDoneHash = {
            [ ACTIVITY_STATUS.NOTSTARTED ]: 0,
            [ ACTIVITY_STATUS.INCOMPLETE ]: InteropService.IncompletePercentDone,
            [ ACTIVITY_STATUS.COMPLETE ]:   100,
            [ ACTIVITY_STATUS.EXCLUDED ]:   0,
        }

        return relativePercentDoneHash[ status ] ?? 0;
    }

    static getPercentDoneToActivityStatus( percentDone: number ): string{

        if( percentDone === 0 )
            return ACTIVITY_STATUS.NOTSTARTED;
        if( percentDone === 100 )
            return ACTIVITY_STATUS.COMPLETE;
        if( percentDone > 0 && percentDone < 100 )
            return ACTIVITY_STATUS.INCOMPLETE;

        return ACTIVITY_STATUS.NOTSTARTED;
    }

    static getDefaultTaskConstraintType( asBryntumEnumvalues = true ): CorvalConstraintType | BryntumConstraintType{

        const defaultType = "startnoearlierthan";

        return asBryntumEnumvalues
               ? defaultType
               : InteropService.getTaskConstraintTypeFromEnumValue( defaultType, true );
    }

    static getTaskConstraintTypes( asBryntumEnumvalues = true ): Array<BryntumConstraintType>{

        return [
            CorvalConstraintType.Finishnoearlierthan,
            CorvalConstraintType.Finishnolaterthan,
            CorvalConstraintType.Mustfinishon,
            CorvalConstraintType.Muststarton,
            CorvalConstraintType.Startnoearlierthan,
            CorvalConstraintType.Startnolaterthan,
            CorvalConstraintType.Assoonaspossible,
            CorvalConstraintType.Aslateaspossible,
        ].map( type => asBryntumEnumvalues ? InteropService.getTaskConstraintTypeFromEnumValue( type as any ) : type );
    }

    static getTaskConstraintTypeFromEnumValue( constraintTypeValue: BryntumConstraintType, searchByMapValue = false ){

        const types = new Map<CorvalConstraintType, BryntumConstraintType>( [
            [ CorvalConstraintType.Finishnoearlierthan, 'finishnoearlierthan' ],
            [ CorvalConstraintType.Finishnolaterthan, 'finishnolaterthan' ],
            [ CorvalConstraintType.Mustfinishon, 'mustfinishon' ],
            [ CorvalConstraintType.Muststarton, 'muststarton' ],
            [ CorvalConstraintType.Startnoearlierthan, 'startnoearlierthan' ],
            [ CorvalConstraintType.Startnolaterthan, 'startnolaterthan' ],
            [ CorvalConstraintType.Assoonaspossible, 'assoonaspossible' ],
            [ CorvalConstraintType.Aslateaspossible, 'aslateaspossible' ],
        ] );

        if( searchByMapValue ){

            return getKeyFromMapValue( types, constraintTypeValue as BryntumConstraintType ) ?? null;
        }

        return types.get( constraintTypeValue as CorvalConstraintType ) ?? null;
    }

    static getTaskConstraintTypeStatement( constraintTypeValue: BryntumConstraintType ){

        const types = new Map<BryntumConstraintType, string>( [
            [ 'finishnoearlierthan', `must finish no earlier than` ],
            [ 'finishnolaterthan', `must finish no later than` ],
            [ 'mustfinishon', `must finish on` ],
            [ 'muststarton', `must start on` ],
            [ 'startnoearlierthan', `must start no earlier than` ],
            [ 'startnolaterthan', `must start no later than` ],
            [ 'assoonaspossible', `starts as soon as possible` ],
            [ 'aslateaspossible', `starts as late as possible` ],
        ] );

        return types.get( constraintTypeValue ) ?? null;
    }

    static getTaskConstraintTypeStatementGenerator( constraintTypeValue: BryntumConstraintType ){

        const types = new Map<BryntumConstraintType, ( subject: string, date?: string ) => string>( [
            [ 'finishnoearlierthan', ( subject, date ) => `${ subject } ${ InteropService.getTaskConstraintTypeStatement( constraintTypeValue ) } ${ date }.` ],
            [ 'finishnolaterthan', ( subject, date ) => `${ subject } ${ InteropService.getTaskConstraintTypeStatement( constraintTypeValue ) } ${ date }.` ],
            [ 'mustfinishon', ( subject, date ) => `${ subject } ${ InteropService.getTaskConstraintTypeStatement( constraintTypeValue ) } ${ date }.` ],
            [ 'muststarton', ( subject, date ) => `${ subject } ${ InteropService.getTaskConstraintTypeStatement( constraintTypeValue ) } ${ date }.` ],
            [ 'startnoearlierthan', ( subject, date ) => `${ subject } ${ InteropService.getTaskConstraintTypeStatement( constraintTypeValue ) } ${ date }.` ],
            [ 'startnolaterthan', ( subject, date ) => `${ subject } ${ InteropService.getTaskConstraintTypeStatement( constraintTypeValue ) } ${ date }.` ],
            [ 'assoonaspossible', ( subject ) => `${ subject } ${ InteropService.getTaskConstraintTypeStatement( constraintTypeValue ) }.` ],
            [ 'aslateaspossible', ( subject ) => `${ subject } ${ InteropService.getTaskConstraintTypeStatement( constraintTypeValue ) }.` ],
        ] );

        return types.get( constraintTypeValue ) ?? null;
    }
}
